/**
 * Client-side model for content-provider accounts (docs/094-CONTENT-PROVIDER-ACCOUNTS.md
 * §1–2, Part 1 core plumbing). A `Provider` is one sign-in the app holds to a content
 * provider's backend — yours (`expo.extra.backendUrl`, seeded as the first provider in
 * Part 2) or someone else's, since the contract (`GET /catalogs`, worker/src/catalogs.ts)
 * is the same either way. `GrantedCatalog` is one manifest URL that provider's
 * `GET /catalogs` said the signed-in user is entitled to.
 *
 * This file is pure data + a lenient parse of the `/catalogs` response — no framework
 * dependency, no storage, no UI. Wiring it into `appState`/screens is Part 2.
 */
import { z } from "zod";
import { ParseResult, err, ok } from "../core/result";

/**
 * v0 ships the "compatible backend" contract; `"oidc"` is the browser-based auth kind
 * added by docs/215-PROVIDER-LOGIN-SECURITY-OIDC.md §3a (pure core in v0 — the native
 * sign-in flow itself is v1). `"social"` (docs/292-PROVIDER-RETURNING-USER-APPLE-
 * IDENTITY.md v0 §6.1, Option B) is a native social sign-in (Apple, v1 Google)
 * relayed to the provider's existing `POST /auth/apple`-style endpoint — it carries
 * an opaque `session`, exactly like `"contract"`, so `GET /catalogs` +
 * `/auth/refresh` (`refreshContractProviderCatalogs`) work unchanged; the only thing
 * that differs from `"contract"` is HOW you re-sign-in (a native picker, not email/
 * password). Additive: an existing `"contract"`/`"oidc"` provider parses unchanged.
 */
export type ProviderAuthKind = "contract" | "oidc" | "social";

/** Which social identity provider a `"social"` `Provider` authenticated through
 * (docs/292 v0 — `"apple"` today, `"google"` v1). Present only on a `"social"`
 * provider. */
export type SocialProviderKind = "apple" | "google";

/**
 * The OIDC client config for one `"oidc"` provider (docs/215 §3a/§5) — everything
 * `buildAuthorizationUrl`/token exchange need, resolved from that provider's discovery
 * document + the app's own registered `client_id`/redirect. Not secret (a public OAuth
 * client has no client secret at all, docs/215 §2.2) — safe alongside `Provider`'s other
 * plain metadata.
 *
 * `clientId` is **optional** as of docs/215 v2 §3 (Dynamic Client Registration, RFC
 * 7591): a provider whose discovery document advertises a `registration_endpoint` lets
 * `runOidcSignIn` (`auth/oidcSignIn.ts`) self-register and obtain one instead of
 * requiring it configured up front. Every provider that publishes a static `client_id`
 * via `GET /provider` (the common case, docs/215 §2.3's "static registration" path)
 * still sets it here as before — this is additive, not a behavior change for them.
 */
export interface OidcConfig {
  issuer: string;
  clientId?: string;
  redirectUri: string;
  scopes: string[];
}

export interface GrantedCatalog {
  manifestUrl: string;
  /** Optional — the app resolves the real title from the fetched manifest either way. */
  title?: string;
  /**
   * Optional per-catalog content token. v0's `GET /catalogs` mints **one** token for
   * the whole entitled set (see `CatalogsResponse.token`) instead of per-catalog
   * tokens, so this is normally unset until a v1 provider starts sending them.
   */
  token?: string;
}

export interface Provider {
  /** Stable local id (generated when the provider is added) — not the provider's own
   * user id, so the same provider account could in principle be added twice under
   * different local ids without colliding. */
  id: string;
  name: string;
  baseUrl: string;
  authKind: ProviderAuthKind;
  /** Opaque session/refresh token; absent when signed out. A secret — secure-store,
   * never plain storage (see `providerRegistryStore.ts`). Used by `"contract"` AND
   * `"social"` providers (docs/292 v0 — a social sign-in mints the SAME opaque
   * session a contract sign-in does); an `"oidc"` provider uses
   * `accessToken`/`refreshToken` instead. */
  session?: string;
  /** Last-fetched entitled catalogs, cached so the tree can render offline. */
  catalogs: GrantedCatalog[];
  /** Present only when `authKind === "social"` (docs/292 v0) — which social identity
   * provider this connection went through. */
  socialProvider?: SocialProviderKind;
  /** OIDC client config (docs/215 §3a) — present only for an `"oidc"` provider. */
  oidc?: OidcConfig;
  /** OIDC access token — a secret, secure-store only (see `providerRegistryStore.ts`),
   * same isolation as `session`. Absent when signed out or for a `"contract"` provider. */
  accessToken?: string;
  /** OIDC refresh token — a secret, same handling as `accessToken`. Some issuers don't
   * return one (e.g. no offline access requested), so this stays optional even for a
   * signed-in `"oidc"` provider. */
  refreshToken?: string;
  /** Epoch seconds `accessToken` expires. NOT a secret (just a schedule hint for
   * `auth/oidc.ts`'s `refreshDueAt`) — rides the metadata blob, unlike the tokens
   * themselves. */
  accessTokenExpiresAt?: number;
  /**
   * A human-readable identity for this signed-in account — an email address or display
   * name — surfaced on the docs/294-PROFILE-ACCOUNT-SWITCHING.md v1 "Manage Accounts"
   * page (and, when set, the `ProfilePicker`'s `AccountsSection`) alongside the provider's
   * own name, so a user with several connected accounts can tell them apart by *who*
   * they're signed in as, not just *which provider*. NOT a secret — rides the metadata
   * blob like `name`/`baseUrl`.
   *
   * **Not yet populated by anything** — this is a forward-ready field only: no sign-in
   * flow (contract, OIDC, or social) fetches a userinfo/whoami endpoint today, so it
   * stays `undefined` until a future userinfo fetch wires it up (docs/294 v1 follow-up).
   * Invite-based accounts have no real name today, so a provider row simply omits this
   * line when it's unset. Additive/optional — an existing stored `Provider` with no
   * `accountLabel` parses unchanged (docs/108-safe).
   */
  accountLabel?: string;
  /**
   * Opt-in "show this provider's catalogs at root" flag (docs/289-FLATTEN-PROVIDER-
   * CATALOGS-TO-ROOT.md v0, D1) — a per-provider toggle set on the Providers page.
   * `undefined`/`false` (every provider before this feature, and the default for a
   * newly-added one) = today's nested behavior: `useLoadCatalogTree`'s provider loop
   * builds ONE aggregating `provider:<id>` root you tap into. `true` = the loop
   * instead pushes one top-level root PER granted catalog (`grantRootFromResolved`,
   * `providerCatalogLoader.ts`), each still tagged with `providerId`/`providerName`
   * so provenance (the "Provider · {name}" row label, docs/276 grouping) is
   * unchanged either way. NOT a secret — rides the metadata blob like `name`/
   * `baseUrl`. See that doc's D2/D3 for how flattening composes with dedup/Spaces.
   */
  showAtRoot?: boolean;
}

/** The shape `GET /catalogs` (worker/src/catalogs.ts) actually returns. */
export interface CatalogsResponse {
  catalogs: { manifestUrl: string; title?: string; token?: string }[];
  /** One content token covering the whole entitled set (v0 — see docs/094 §2 known issue). */
  token: string;
  expiresAt: number;
  entitlements?: { member: boolean; teacher: boolean; packs: string[] };
}

const CatalogsResponseSchema = z.object({
  catalogs: z.array(
    z.object({
      manifestUrl: z.string().min(1),
      title: z.string().optional(),
      token: z.string().optional(),
    }),
  ),
  token: z.string().min(1),
  expiresAt: z.number(),
  entitlements: z
    .object({
      member: z.boolean(),
      teacher: z.boolean(),
      packs: z.array(z.string()),
    })
    .optional(),
});

/**
 * Parses a raw `GET /catalogs` response body. Deliberately a `ParseResult` (unlike the
 * lenient device-sync payload parse) — this is a live network response the caller
 * needs to know failed, not local storage to degrade gracefully from.
 */
export function parseCatalogsResponse(input: unknown): ParseResult<CatalogsResponse> {
  const parsed = CatalogsResponseSchema.safeParse(input);
  if (!parsed.success) {
    return err(parsed.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`));
  }
  return ok(parsed.data);
}

/**
 * Turn a parsed `GET /catalogs` response into the `GrantedCatalog[]` cached on a
 * `Provider` (Part 2 app wiring) — stamping the response's whole-set `token` onto
 * every entry that doesn't already carry its own. v0 providers (including this
 * app's own Worker) mint one token for the whole entitled set, so every entry ends
 * up with the same token; a v1 provider that starts returning per-catalog tokens
 * is respected as-is (its own `token` wins over the whole-set one).
 */
export function grantedCatalogsFromResponse(res: CatalogsResponse): GrantedCatalog[] {
  return res.catalogs.map((c) => ({
    manifestUrl: c.manifestUrl,
    ...(c.title ? { title: c.title } : {}),
    token: c.token ?? res.token,
  }));
}
